import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import { AuthUIProviderTanstack } from "@daveyplate/better-auth-ui/tanstack";
import { useEntitlement } from "@planvera/app-commons/subscription";
import type { QueryClient as ReactQueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	Outlet,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { PlannerDrawingLoadingOverlay } from "@/components/planner-loading";
import { Toaster } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";
import type { orpc } from "@/utils/orpc";
import { orpc as orpcUtils } from "@/utils/orpc";

const APP_ID = import.meta.env.VITE_APP_ID;

export interface RouterAppContext {
	orpc: typeof orpc;
	queryClient: ReactQueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
	component: RootComponent,
});

function RootComponent() {
	const { data: session, isPending: isSessionLoading } =
		authClient.useSession();
	const isDrawingRoute = useRouterState({
		select: (state) => state.location.pathname.includes("/drawing/"),
	});

	if (isSessionLoading) {
		// On a drawing-viewer reload, render the same loading stepper the viewer
		// shows so the whole session-check → query → render sequence is one
		// continuous loading UI instead of a text flash before the stepper.
		if (isDrawingRoute) {
			return (
				<main className="relative flex h-screen flex-col overflow-hidden bg-background text-foreground">
					<PlannerDrawingLoadingOverlay stage="initializing" />
				</main>
			);
		}
		return (
			<div className="flex h-screen items-center justify-center">
				<p className="text-muted-foreground">Loading planner...</p>
			</div>
		);
	}

	if (!session?.user) {
		const webUrl = import.meta.env.VITE_WEB_URL || "https://localhost:3000";
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-center">
					<h1 className="font-bold text-2xl">Sign in required</h1>
					<p className="mt-2 text-muted-foreground">
						Please sign in to PlanVera to open Planner.
					</p>
					<a
						href={`${webUrl}/auth/sign-in`}
						className="mt-4 inline-block text-primary hover:underline"
					>
						Go to Sign In
					</a>
				</div>
			</div>
		);
	}

	return (
		<AuthQueryProvider>
			<AuthUIProviderTanstack authClient={authClient}>
				<ThemeProvider
					attribute="class"
					defaultTheme="light"
					disableTransitionOnChange
					storageKey="planner-theme"
				>
					<SubscriptionGateWrapper>
						<Outlet />
					</SubscriptionGateWrapper>
					<Toaster closeButton richColors />
				</ThemeProvider>
			</AuthUIProviderTanstack>
		</AuthQueryProvider>
	);
}

function SubscriptionGateWrapper({ children }: { children: ReactNode }) {
	// PLA-302: org id is resolved server-side from the drawing's project,
	// not from session active-org. Pass drawingId from URL.
	const params = useParams({ strict: false }) as { drawingId?: string };
	const drawingId = params.drawingId;

	const {
		isAllowed,
		isLoading: isCheckingAccess,
		reason,
	} = useEntitlement({
		queryOptions: orpcUtils.entitlements.checkDrawingFeature.queryOptions({
			input: {
				drawingId: drawingId ?? "",
				featureName: APP_ID ?? "",
			},
		}),
		enabled: !!drawingId && !!APP_ID,
	});

	if (!APP_ID) {
		return <>{children}</>;
	}

	// No drawing in URL (e.g. landing/index): render children; subroutes that
	// require entitlement will mount their own gate via the same hook.
	if (!drawingId) {
		return <>{children}</>;
	}

	if (isCheckingAccess) {
		return (
			<div className="flex h-screen items-center justify-center">
				<p className="text-muted-foreground">Checking subscription...</p>
			</div>
		);
	}

	if (!isAllowed) {
		if (reason === "USER_LICENSE_REQUIRED") {
			return (
				<div className="flex h-screen items-center justify-center">
					<div className="text-center">
						<h1 className="font-bold text-2xl">License required</h1>
						<p className="mt-2 text-muted-foreground">
							You need a Planner license to use this tool. Contact your
							organization admin.
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-center">
					<h1 className="font-bold text-2xl">Upgrade required</h1>
					<p className="mt-2 text-muted-foreground">
						Your current plan does not include Planner access.
					</p>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}
